import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, map, Observable, tap } from 'rxjs';
import { ToasterService } from 'shared';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

import { CredentialsVPResponseDto, RevokeCredentialDto } from '../../../app/api';
import { CompaniesService as CompaniesApiService } from '../../../app/api/api/companies.service';
import { CredentialsService as CredentialsApiService } from '../../../app/api/api/credentials.service';
import { CompanyDto } from '../../../app/api/model/companyDto';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private companiesListSignal = signal<CompanyDto[]>([]);
  private loadingSignal = signal(false);
  private errorSignal = signal<string | null>(null);
  private totalSignal = signal(0);
  private pendingCountSignal = signal(0);

  readonly companies = this.companiesListSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly total = this.totalSignal.asReadonly();
  readonly pendingCount = this.pendingCountSignal.asReadonly();

  private companiesApiService = inject(CompaniesApiService);
  private credentialsApiService = inject(CredentialsApiService);
  private toaster = inject(ToasterService);
  private translate = inject(TranslateService);

  constructor() {
    this.fetchPendingCount();
  }

  private fetchPendingCount(): void {
    this.getPendingCompanies().subscribe({
      next: (companies) => {
        console.log('Pending companies count:', companies.length);
        this.pendingCountSignal.set(companies.length);
      },
      error: (err) => {
        console.error('Error fetching pending count:', err);
      },
    });
  }

  fetchCompanies(limit: number = 100, skip: number = 0): void {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    this.companiesApiService
      .companiesControllerGetAllCompanies(limit.toString(), skip.toString())
      .pipe(
        map((response) => response.data!),
        catchError((_err: HttpErrorResponse) => {
          this.errorSignal.set('api-error');
          this.loadingSignal.set(false);
          this.toaster.error({
            message: this.translate.instant('failed-to-load-companies'),
          });
          return EMPTY;
        }),
      )
      .subscribe((data) => {
        this.companiesListSignal.set(data.data || []);
        this.totalSignal.set(data.total || 0);
        this.loadingSignal.set(false);
      });
  }

  getPendingCompanies(): Observable<CompanyDto[]> {
    return this.companiesApiService
      .companiesControllerGetPendingCompanies()
      .pipe(map((response) => response.data?.data || []));
  }

  approveCompany(did: string): Observable<CompanyDto> {
    return this.companiesApiService.companiesControllerApproveCompany(did).pipe(
      map((response) => response.data!),
      tap(() => {
        // Only update pending count, no need to fetch all companies on pending approvals page
        this.fetchPendingCount();
      }),
    );
  }

  rejectCompany(did: string): Observable<CompanyDto> {
    return this.companiesApiService.companiesControllerRejectCompany(did).pipe(
      map((response) => response.data!),
      tap(() => {
        // Only update pending count, no need to fetch all companies on pending approvals page
        this.fetchPendingCount();
      }),
    );
  }

  revokeCompany(did: string): Observable<CompanyDto> {
    return this.companiesApiService.companiesControllerRevokeCompany(did).pipe(
      map((response) => response.data!),
      tap(() => {
        this.fetchCompanies();
      }),
    );
  }

  revokeCredential(credentialId: string): Observable<RevokeCredentialDto> {
    return this.credentialsApiService
      .credentialsControllerRevokeCredential(credentialId)
      .pipe(map((response: any) => response.data ?? response));
  }

  getCompanyCredentials(did: string): Observable<CredentialsVPResponseDto> {
    return this.companiesApiService
      .companiesControllerGetCompanyCredentials(did)
      .pipe(map((response) => response.data!));
  }

  getCompanyByDid(did: string): Observable<CompanyDto> {
    return this.companiesApiService
      .companiesControllerGetCompanyById(did)
      .pipe(map((response) => response.data!));
  }

  getCompanyDidDocument(did: string): Observable<Record<string, any>> {
    return this.companiesApiService.companiesControllerGetCompanyDidDocument(did);
  }
}
